import React, { useEffect, useState } from 'react';
import { Col, Row } from 'react-bootstrap';
import { Button, Input, Modal, Select, Text } from 'v3/components';

const STATUS_OPTIONS = [
  { value: 'all', label: 'Todos' },
  { value: 'active', label: 'Ativos' },
  { value: 'inactive', label: 'Inativos' },
];

const RESPONSE_STATUS_OPTIONS = [
  { value: 'all', label: 'Todos' },
  { value: 'active', label: 'Ativos' },
  { value: 'expired', label: 'Vencidos' },
];

function onlyDigits(value) {
  return String(value || '').replace(/\D/g, '');
}

function maskCpf(value) {
  const digits = onlyDigits(value).slice(0, 11);
  let out = digits;
  if (digits.length > 3) out = `${digits.slice(0, 3)}.${digits.slice(3)}`;
  if (digits.length > 6) {
    out = `${digits.slice(0, 3)}.${digits.slice(3, 6)}.${digits.slice(6)}`;
  }
  if (digits.length > 9) {
    out = `${digits.slice(0, 3)}.${digits.slice(3, 6)}.${digits.slice(
      6,
      9
    )}-${digits.slice(9)}`;
  }
  return out;
}

function maskCnpj(value) {
  const chars = String(value || '')
    .toUpperCase()
    .replace(/[^A-Z0-9]/g, '')
    .slice(0, 14);
  let out = chars;
  if (chars.length > 2) out = `${chars.slice(0, 2)}.${chars.slice(2)}`;
  if (chars.length > 5) {
    out = `${chars.slice(0, 2)}.${chars.slice(2, 5)}.${chars.slice(5)}`;
  }
  if (chars.length > 8) {
    out = `${chars.slice(0, 2)}.${chars.slice(2, 5)}.${chars.slice(
      5,
      8
    )}/${chars.slice(8)}`;
  }
  if (chars.length > 12) {
    out = `${chars.slice(0, 2)}.${chars.slice(2, 5)}.${chars.slice(
      5,
      8
    )}/${chars.slice(8, 12)}-${chars.slice(12)}`;
  }
  return out;
}

function maskCpfCnpj(value) {
  const chars = String(value || '')
    .toUpperCase()
    .replace(/[^A-Z0-9]/g, '')
    .slice(0, 14);
  const hasLetters = /[A-Z]/.test(chars);
  if (!hasLetters && chars.length <= 11) return maskCpf(chars);
  return maskCnpj(chars);
}


function maskPhone(value) {
  const digits = onlyDigits(value).slice(0, 11);
  if (digits.length === 0) return '';
  if (digits.length <= 2) return `(${digits}`;
  const ddd = digits.slice(0, 2);
  const rest = digits.slice(2);
  if (rest.length === 0) return `(${ddd})`;
  if (rest.length <= 4) return `(${ddd}) ${rest}`;
  if (rest.length <= 8) return `(${ddd}) ${rest.slice(0, 4)}-${rest.slice(4)}`;
  return `(${ddd}) ${rest.slice(0, 5)}-${rest.slice(5, 9)}`;
}

function buildSearchResponse(filters) {
  return String(
    filters?.answersSearch ||
    filters?.cpfCnpjSearch ||
    filters?.phoneSearch ||
    ''
  ).trim();
}

export default function TrainingChecklistFilter({
  loading = false,
  onFilterChange,
  showAdvancedFilter = false,
  onCloseAdvanceFilterModal,
  setParentFilter,
  setAdvancedFiltersQtd,
}) {
  const [filter, setFilter] = useState({
    q: '',
    status: 'all',
  });
  const [advancedFilters, setAdvancedFilters] = useState({
    plates: [],
    answersSearch: '',
    cpfCnpjSearch: '',
    phoneSearch: '',
    responseStatus: 'all',
  });

  useEffect(() => {
    if (!setAdvancedFiltersQtd) return;
    const count = [
      advancedFilters.answersSearch,
      advancedFilters.cpfCnpjSearch,
      advancedFilters.phoneSearch,
      advancedFilters.responseStatus !== 'all'
        ? advancedFilters.responseStatus
        : null,
      Array.isArray(advancedFilters.plates) && advancedFilters.plates.length > 0
        ? 'plates'
        : null,
    ].filter(Boolean).length;

    setAdvancedFiltersQtd(count);
  }, [advancedFilters, setAdvancedFiltersQtd]);

  function applyFilters(nextFilter = filter) {
    const payload = {
      q: String(nextFilter.q || '').trim(),
      plates: Array.isArray(advancedFilters.plates)
        ? advancedFilters.plates
        : [],
      searchResponse: buildSearchResponse(advancedFilters),
      responseStatus:
        advancedFilters.responseStatus === 'all'
          ? undefined
          : advancedFilters.responseStatus,
      isActive:
        nextFilter.status === 'all'
          ? undefined
          : nextFilter.status === 'active',
    };

    if (setParentFilter) setParentFilter(payload);
    if (onFilterChange) onFilterChange(payload);
    if (onCloseAdvanceFilterModal) onCloseAdvanceFilterModal();
  }

  return (
    <>
      <Modal
        show={showAdvancedFilter}
        handleClose={() => {
          if (setParentFilter) {
            setParentFilter(old => ({
              ...old,
              plates: advancedFilters.plates,
              searchResponse: buildSearchResponse(advancedFilters),
              responseStatus:
                advancedFilters.responseStatus === 'all'
                  ? undefined
                  : advancedFilters.responseStatus,
            }));
          }
          if (onCloseAdvanceFilterModal) onCloseAdvanceFilterModal();
        }}
        heading="Filtro avançado"
        body={
          <Row>
            <Col md={3} sm={12} className="my-1">
              <Input
                label="Busca nas respostas"
                placeholder="Digite um termo das respostas"
                value={advancedFilters.answersSearch}
                onChange={event => {
                  const value = event?.target?.value || '';

                  setAdvancedFilters(old => ({
                    ...old,
                    answersSearch: value,
                    cpfCnpjSearch: '',
                    phoneSearch: '',
                  }));
                }}
                disabled={loading}
              />
            </Col>

            <Col md={3} sm={12} className="my-1">
              <Input
                label="Busca por CPF/CNPJ"
                placeholder="Digite um CPF ou CNPJ"
                value={advancedFilters.cpfCnpjSearch}
                onChange={event => {
                  const value = maskCpfCnpj(event?.target?.value || '');

                  setAdvancedFilters(old => ({
                    ...old,
                    answersSearch: '',
                    cpfCnpjSearch: value,
                    phoneSearch: '',
                  }));
                }}
                disabled={loading}
              />
            </Col>

            <Col md={3} sm={12} className="my-1">
              <Input
                label="Busca por Telefone"
                placeholder="Digite um telefone"
                value={advancedFilters.phoneSearch}
                onChange={event => {
                  const value = maskPhone(event?.target?.value || '');

                  setAdvancedFilters(old => ({
                    ...old,
                    answersSearch: '',
                    cpfCnpjSearch: '',
                    phoneSearch: value,
                  }));
                }}
                disabled={loading}
              />
            </Col>

            <Col md={3} sm={12} className="my-1">
              <Select
                label="Vencimento da resposta"
                placeholder="Todos"
                options={RESPONSE_STATUS_OPTIONS}
                value={
                  RESPONSE_STATUS_OPTIONS.find(
                    option => option.value === advancedFilters.responseStatus
                  ) || RESPONSE_STATUS_OPTIONS[0]
                }
                onChange={value =>
                  setAdvancedFilters(old => ({
                    ...old,
                    responseStatus: value?.value || 'all',
                  }))
                }
                disableSearch
                disableClear
              />
            </Col>
          </Row>
        }
        footer={
          <Row style={{ justifyContent: 'flex-end' }}>
            <Button
              variant="primary"
              disabled={loading}
              onClick={() => applyFilters()}
            >
              <Text color="white" weight={500}>
                Filtrar
              </Text>
            </Button>
          </Row>
        }
      />

      <Row className="my-4">
        <Col md={8} sm={12} className="my-1">
          <Input
            label="Nome"
            labelColor="#fff"
            placeholder="Buscar por título ou responsável..."
            value={filter.q}
            disabled={loading}
            onChange={event => {
              const value = event?.target?.value || '';

              setFilter(old => ({
                ...old,
                q: value,
              }));
            }}
            onKeyDown={event => {
              if (event?.key === 'Enter') {
                event.preventDefault();
                applyFilters();
              }
            }}
          />
        </Col>

        <Col md={3} sm={12} className="my-1">
          <Select
            label="Situação"
            labelTextColor="#fff"
            placeholder="Todos"
            options={STATUS_OPTIONS}
            value={
              STATUS_OPTIONS.find(option => option.value === filter.status) ||
              STATUS_OPTIONS[0]
            }
            onChange={value =>
              setFilter(old => ({
                ...old,
                status: value?.value || 'all',
              }))
            }
            disableSearch
            disableClear
          />
        </Col>

        <Col
          md={1}
          sm={12}
          className="my-1"
          style={{ display: 'flex', alignItems: 'end' }}
        >
          <Button
            style={{ flex: 1 }}
            variant="success"
            disabled={loading}
            onClick={() => applyFilters()}
          >
            <Text type="regular" weight={500}>
              Filtrar
            </Text>
          </Button>
        </Col>
      </Row>
    </>
  );
}
